/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx, keyframes } from "@emotion/react";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";

import type { FC } from "react";

export const VERIFICATION_CODE_LENGTH = 6;

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  isError: boolean;
  labelId: string;
};

/**
 * 本物の input を6つ並べると、iOS / Android がキーボード上部に出すSMSの
 * 自動入力候補やパスワードマネージャが正しく効かないため、
 * 単一入力を保ったまま見た目だけ分割する作りにしている。
 */
export const VerificationCodeInput: FC<Props> = ({
  value,
  onChange,
  disabled,
  isError,
  labelId,
}) => {
  const classes = styles();

  return (
    <OTPInput
      maxLength={VERIFICATION_CODE_LENGTH}
      value={value}
      onChange={onChange}
      disabled={disabled}
      pattern={REGEXP_ONLY_DIGITS}
      inputMode="numeric"
      autoComplete="one-time-code"
      aria-labelledby={labelId}
      render={({ slots }) => (
        <div css={classes.container}>
          {slots.map((slot, index) => (
            <div
              key={index}
              css={[
                classes.slot,
                slot.isActive && classes.activeSlot,
                isError && classes.errorSlot,
                disabled && classes.disabledSlot,
              ]}
            >
              {slot.char}
              {slot.hasFakeCaret && <span css={classes.caret} />}
            </div>
          ))}
        </div>
      )}
    />
  );
};

const blink = keyframes({
  "0%, 49%": { opacity: 1 },
  "50%, 100%": { opacity: 0 },
});

const styles = () => {
  return {
    container: css({
      display: "flex",
      gap: "8px",
    }),
    slot: css({
      position: "relative",
      flex: "1 1 0",
      minWidth: 0,
      maxWidth: "48px",
      height: "56px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      fontWeight: "bold",
      lineHeight: "32px",
      border: "1px solid #d2d2d2",
      backgroundColor: "#fff",
    }),
    activeSlot: css({
      border: "2px solid #57bfae",
    }),
    errorSlot: css({
      border: "2px solid #e53935",
    }),
    disabledSlot: css({
      backgroundColor: "#f7f7f7",
    }),
    caret: css({
      position: "absolute",
      width: "2px",
      height: "26px",
      backgroundColor: "#57bfae",
      animation: `${blink} 1s step-end infinite`,
    }),
  };
};
